/**
 * MeteoPanel — Componente reutilizable de Consulta meteorológica.
 *
 * Usado en dos contextos:
 *  1. Página independiente `/meteo` (consultas sueltas sin expediente).
 *  2. Pestaña dentro del expediente — auto-rellena dirección y fecha del
 *     siniestro y, al generar el PDF, lo adjunta automáticamente al
 *     expediente como "Evidencia meteo".
 *
 * Fuentes (avisadas al usuario por transparencia):
 *  - Geocoding: OpenStreetMap Nominatim
 *  - Weather: Open-Meteo (ERA5 reanálisis para histórico)
 *  - Estación oficial cercana: AEMET OpenData
 *  - Visor de rayos complementario: mapaderayos.com (enlace clicable)
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CloudLightning, MapPin, CalendarBlank, MagnifyingGlass, DownloadSimple,
  Thermometer, Wind, CloudRain, Info, ArrowSquareOut, Cloud,
} from "@phosphor-icons/react";

const SEVERE_WIND_KMH = 80;
const SEVERE_PRECIP_MM = 40;
const MAX_RANGE_DAYS = 10;

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function diffDays(a, b) {
  const da = new Date(`${a}T12:00:00`);
  const db = new Date(`${b}T12:00:00`);
  return Math.floor((db - da) / 86400000) + 1;
}

export default function MeteoPanel({
  expedienteId = null,
  defaultAddress = "",
  defaultDate = "",
  onAttached = null,
}) {
  const [address, setAddress] = useState(defaultAddress);
  const [date, setDate] = useState(defaultDate || todayISO());
  const [endDate, setEndDate] = useState("");           // vacío = búsqueda de día único
  const [rangeMode, setRangeMode] = useState(false);    // toggle rango
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  // Mapas de rayos opcionales para el PDF:
  // - LightningMaps: archivo HISTÓRICO por día → marcado por defecto.
  // - mapaderayos.com: visor EN VIVO → solo se ofrece si la fecha consultada
  //   es de las últimas 24h, y arranca desmarcado.
  const [includeLightningmaps, setIncludeLightningmaps] = useState(true);
  const [includeMapaderayos, setIncludeMapaderayos] = useState(false);

  // Variables Meteosim a anexar como PDFs oficiales (se ejecuta scraping
  // Playwright con las credenciales corporativas almacenadas cifradas).
  const [meteosimVars, setMeteosimVars] = useState({
    rayos: false,
    racha_viento: false,
    prec_acumulada: false,
    prec_intensidad: false,
  });
  const [meteosimStatus, setMeteosimStatus] = useState(null);
  useEffect(() => {
    // Comprobamos si el admin ha configurado credenciales — sólo para
    // mostrar/ocultar la sección. Si falla (perito sin permisos), asumimos
    // no configurada (oculto).
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/integrations/meteosim");
        if (!cancel) setMeteosimStatus(r.data?.status || null);
      } catch {
        if (!cancel) setMeteosimStatus(null);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const isMapaderayosEligible = useMemo(() => {
    // Sólo tiene sentido la captura en vivo si la fecha consultada (o el
    // fin del rango) está dentro de las últimas 24h.
    const refIso = rangeMode && endDate ? endDate : date;
    if (!refIso) return false;
    const ref = new Date(`${refIso}T23:59:59`);
    const diffHrs = (Date.now() - ref.getTime()) / 36e5;
    return diffHrs <= 24;
  }, [date, endDate, rangeMode]);

  // Si la fecha cambia y deja de ser elegible, desmarca el checkbox.
  useEffect(() => {
    if (!isMapaderayosEligible && includeMapaderayos) {
      setIncludeMapaderayos(false);
    }
  }, [isMapaderayosEligible, includeMapaderayos]);

  // Auto-busca al montar si ya hay dirección + fecha (caso dentro del expediente).
  useEffect(() => {
    if (defaultAddress && defaultDate) {
      runLookup(defaultAddress, defaultDate, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddress, defaultDate]);

  const runLookup = async (a = address, d = date, e = (rangeMode ? endDate : "")) => {
    if (!a || a.trim().length < 3) {
      toast.error("Indica una dirección"); return;
    }
    if (!d) { toast.error("Indica la fecha inicial"); return; }
    if (e) {
      if (e < d) { toast.error("La fecha fin no puede ser anterior a la inicio"); return; }
      if (diffDays(d, e) > MAX_RANGE_DAYS) {
        toast.error(`La ventana máxima es de ${MAX_RANGE_DAYS} días`); return;
      }
    }
    setLoading(true);
    try {
      const params = { address: a.trim(), date: d };
      if (e) params.end_date = e;
      const r = await api.get("/meteo/lookup", { params });
      setResult(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo consultar la meteorología";
      toast.error(msg);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    runLookup();
  };

  const onGeneratePdf = async () => {
    if (!result) { toast.error("Primero realiza una consulta"); return; }
    setGenerating(true);
    try {
      const body = {
        address: address.trim(),
        date,
        expediente_id: expedienteId || null,
        include_lightningmaps: !!includeLightningmaps,
        include_mapaderayos: !!(includeMapaderayos && isMapaderayosEligible),
        meteosim_variables: Object.entries(meteosimVars)
          .filter(([, v]) => v).map(([k]) => k),
      };
      if (rangeMode && endDate) body.end_date = endDate;
      const r = await api.post("/meteo/report", body, { responseType: "blob" });

      // Descarga local
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `meteo_${date}.pdf`;
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);

      const attId = r.headers["x-attachment-id"];
      if (expedienteId && attId) {
        toast.success("Informe meteo generado y adjuntado al expediente");
        if (onAttached) onAttached(attId);
      } else {
        toast.success("Informe meteo descargado");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error generando el informe";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="meteo-panel">
      <SearchBar
        address={address} setAddress={setAddress}
        date={date} setDate={setDate}
        endDate={endDate} setEndDate={setEndDate}
        rangeMode={rangeMode} setRangeMode={setRangeMode}
        loading={loading}
        onSubmit={onSubmit}
      />

      {loading && <SkeletonState />}
      {!loading && result && (
        <ResultView
          result={result}
          expedienteId={expedienteId}
          onGeneratePdf={onGeneratePdf}
          generating={generating}
          includeLightningmaps={includeLightningmaps}
          setIncludeLightningmaps={setIncludeLightningmaps}
          includeMapaderayos={includeMapaderayos}
          setIncludeMapaderayos={setIncludeMapaderayos}
          isMapaderayosEligible={isMapaderayosEligible}
          meteosimVars={meteosimVars}
          setMeteosimVars={setMeteosimVars}
          meteosimStatus={meteosimStatus}
        />
      )}
      {!loading && !result && (
        <EmptyHint expedienteId={expedienteId} />
      )}
    </div>
  );
}

// ============================================================
// Sub-componentes
// ============================================================

function SearchBar({ address, setAddress, date, setDate,
                       endDate, setEndDate, rangeMode, setRangeMode,
                       loading, onSubmit }) {
  // Cuando el usuario activa rango, sugerimos fecha+1 si no hay end_date.
  const toggleRange = () => {
    if (!rangeMode) {
      // entering range mode → default 1-day window
      if (!endDate) {
        const d = new Date(`${date}T12:00:00`);
        d.setDate(d.getDate() + 1);
        setEndDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
      }
    } else {
      setEndDate("");
    }
    setRangeMode(!rangeMode);
  };
  return (
    <form onSubmit={onSubmit}
          className="bg-white rounded-[10px] border border-border p-4 md:p-5"
          data-testid="meteo-search-bar">
      <div className="flex items-center justify-between mb-3">
        <span className="field-label">Buscar por</span>
        <div className="inline-flex rounded-md border border-border overflow-hidden text-[12px] font-semibold">
          <button type="button" onClick={() => rangeMode && toggleRange()}
                  data-testid="meteo-mode-single"
                  className={`px-3 py-1.5 ${!rangeMode ? "bg-foreground text-white" : "bg-white text-muted hover:text-foreground"}`}>
            Día único
          </button>
          <button type="button" onClick={() => !rangeMode && toggleRange()}
                  data-testid="meteo-mode-range"
                  className={`px-3 py-1.5 ${rangeMode ? "bg-foreground text-white" : "bg-white text-muted hover:text-foreground"}`}>
            Rango (máx. 10 días)
          </button>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3 items-end">
        <div className={`col-span-12 ${rangeMode ? "md:col-span-5" : "md:col-span-7"}`}>
          <label className="field-label block mb-1">Dirección a consultar</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="text" placeholder="Calle, número, ciudad…"
              value={address} onChange={(e) => setAddress(e.target.value)}
              className="!pl-9" data-testid="meteo-address-input"
              required minLength={3}
            />
          </div>
        </div>
        <div className={`col-span-7 ${rangeMode ? "md:col-span-2" : "md:col-span-3"}`}>
          <label className="field-label block mb-1">{rangeMode ? "Desde" : "Fecha"}</label>
          <div className="relative">
            <CalendarBlank className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="!pl-9" data-testid="meteo-date-input" required />
          </div>
        </div>
        {rangeMode && (
          <div className="col-span-7 md:col-span-3">
            <label className="field-label block mb-1">Hasta (incluida)</label>
            <div className="relative">
              <CalendarBlank className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input type="date" value={endDate}
                     onChange={(e) => setEndDate(e.target.value)}
                     min={date}
                     className="!pl-9" data-testid="meteo-end-date-input" required />
            </div>
          </div>
        )}
        <div className="col-span-5 md:col-span-2">
          <button type="submit"
                  disabled={loading}
                  data-testid="meteo-search-btn"
                  className="w-full bg-foreground text-white text-[13px] h-10 rounded-md font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
            {loading
              ? "Consultando…"
              : <><MagnifyingGlass className="w-4 h-4" weight="bold" /> Consultar</>}
          </button>
        </div>
      </div>
      {rangeMode && (
        <p className="help-text mt-2">
          Ventana máxima de {MAX_RANGE_DAYS} días. El PDF incluirá un detalle por día y resaltará en rojo los días con
          viento o ráfaga ≥ {SEVERE_WIND_KMH} km/h, o precipitación &gt; {SEVERE_PRECIP_MM} mm.
        </p>
      )}
    </form>
  );
}

function EmptyHint({ expedienteId }) {
  return (
    <div className="bg-white rounded-[10px] border border-dashed border-border p-8 text-center"
         data-testid="meteo-empty">
      <CloudLightning className="w-10 h-10 mx-auto text-muted mb-3" weight="thin" />
      <h3 className="text-foreground font-semibold mb-1">Consulta meteorológica</h3>
      <p className="help-text max-w-md mx-auto">
        Introduce dirección y fecha para obtener viento, lluvia, ráfagas y fenómenos
        observados. Genera un PDF firmado con el sello del gabinete
        {expedienteId ? " y se adjuntará automáticamente al expediente." : "."}
      </p>
    </div>
  );
}

function SkeletonState() {
  return (
    <div className="bg-white rounded-[10px] border border-border p-8 text-center"
         data-testid="meteo-loading">
      <Cloud className="w-8 h-8 mx-auto text-muted animate-pulse mb-2" weight="duotone" />
      <p className="help-text">Consultando estaciones meteorológicas…</p>
    </div>
  );
}

function ResultView({ result, expedienteId, onGeneratePdf, generating,
                       includeLightningmaps, setIncludeLightningmaps,
                       includeMapaderayos, setIncludeMapaderayos,
                       isMapaderayosEligible,
                       meteosimVars, setMeteosimVars, meteosimStatus }) {
  const { weather, aemet_station, mapaderayos_url, address_resolved, lat, lon, is_range, end_date } = result;
  if (!weather?.available) {
    return (
      <div className="bg-white rounded-[10px] border border-status-error-bd p-6 text-center"
           data-testid="meteo-error">
        <p className="text-status-error-fg font-semibold mb-1">No hay datos disponibles para esta fecha</p>
        <p className="help-text">{weather?.error || "Open-Meteo no devolvió información."}</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="meteo-result">
      <ResolvedLocation
        address={address_resolved} lat={lat} lon={lon}
        date={result.date} endDate={end_date} isRange={is_range}
        aemet={aemet_station}
      />
      <WeatherSummary weather={weather} />
      {weather.range && weather.days && (
        <DaysTable days={weather.days} />
      )}
      <ExternalLinks lat={lat} lon={lon} mapaderayosUrl={mapaderayos_url} />
      <LightningMapsOptions
        includeLightningmaps={includeLightningmaps}
        setIncludeLightningmaps={setIncludeLightningmaps}
        includeMapaderayos={includeMapaderayos}
        setIncludeMapaderayos={setIncludeMapaderayos}
        isMapaderayosEligible={isMapaderayosEligible}
      />
      <MeteosimOptions
        vars={meteosimVars}
        setVars={setMeteosimVars}
        status={meteosimStatus}
      />
      <PdfFooter
        expedienteId={expedienteId}
        onGenerate={onGeneratePdf}
        generating={generating}
      />
    </div>
  );
}

function ResolvedLocation({ address, lat, lon, date, endDate, isRange, aemet }) {
  const fechaES = useMemo(() => {
    try {
      const fmt = (s) => new Date(`${s}T12:00:00`).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
      if (isRange && endDate) {
        return `Del ${fmt(date)} al ${fmt(endDate)}`;
      }
      return fmt(date);
    } catch { return date; }
  }, [date, endDate, isRange]);
  return (
    <section className="bg-white rounded-[10px] border border-border p-4 md:p-5"
             data-testid="meteo-resolved-location">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6">
          <span className="field-label">Dirección resuelta</span>
          <p className="text-foreground font-medium text-[14px] mt-0.5">{address}</p>
          <p className="text-muted text-[11px] font-mono mt-1">
            {Number(lat).toFixed(5)}, {Number(lon).toFixed(5)}
          </p>
        </div>
        <div className="col-span-6 md:col-span-3">
          <span className="field-label">{isRange ? "Periodo" : "Fecha"}</span>
          <p className="text-foreground font-semibold text-[14px] mt-0.5">{fechaES}</p>
        </div>
        <div className="col-span-6 md:col-span-3">
          <span className="field-label">Estación AEMET</span>
          {aemet ? (
            <div className="mt-0.5">
              <p className="text-foreground font-medium text-[13px]">{aemet.name}</p>
              <p className="text-muted text-[11px]">{aemet.provincia} · {aemet.distance_km} km</p>
            </div>
          ) : (
            <p className="text-muted text-[12px] italic mt-0.5">No disponible</p>
          )}
        </div>
      </div>
    </section>
  );
}

function WeatherSummary({ weather }) {
  // Para el modo rango calculamos Tª min/max a partir de los días.
  let tmin = weather.temperatura_min, tmax = weather.temperatura_max;
  if (weather.range && Array.isArray(weather.days)) {
    const tmins = weather.days.map((d) => d.temperatura_min).filter((v) => v != null);
    const tmaxs = weather.days.map((d) => d.temperatura_max).filter((v) => v != null);
    if (tmins.length) tmin = Math.min(...tmins);
    if (tmaxs.length) tmax = Math.max(...tmaxs);
  }
  const viento = weather.viento_max_kmh;
  const rafaga = weather.rafaga_max_kmh;
  const precip = weather.precipitacion_mm ?? 0;
  const vientoAlert = viento != null && Number(viento) >= SEVERE_WIND_KMH;
  const rafagaAlert = rafaga != null && Number(rafaga) >= SEVERE_WIND_KMH;
  const precipAlert = precip != null && Number(precip) > SEVERE_PRECIP_MM;

  const cards = [
    {
      icon: Wind,
      label: "Viento máx.",
      value: viento != null ? `${Number(viento).toFixed(1)}` : "—",
      unit: "km/h",
      sub: `Dirección dominante ${weather.viento_direccion_text || "—"}`,
      tone: "brand",
      alert: vientoAlert,
    },
    {
      icon: Wind,
      label: "Ráfaga máx.",
      value: rafaga != null ? `${Number(rafaga).toFixed(1)}` : "—",
      unit: "km/h",
      sub: "Pico horario",
      tone: "amber",
      alert: rafagaAlert,
    },
    {
      icon: CloudRain,
      label: "Precipitación",
      value: precip != null ? `${Number(precip).toFixed(1)}` : "0",
      unit: "mm",
      sub: `${Math.round(weather.precipitacion_horas || 0)}h con lluvia · ${weather.horas_tormenta || 0}h con tormenta`,
      tone: "teal",
      alert: precipAlert,
    },
    {
      icon: Thermometer,
      label: "Temperatura",
      value: `${tmin != null ? Number(tmin).toFixed(1) : "—"} / ${tmax != null ? Number(tmax).toFixed(1) : "—"}`,
      unit: "°C",
      sub: "Mín. / Máx.",
      tone: "purple",
      alert: false,
    },
  ];
  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="meteo-summary-grid">
      {cards.map((c) => {
        const Icon = c.icon;
        const wrapperCls = c.alert
          ? "rounded-[10px] border border-red-600 p-4 text-white"
          : "bg-white rounded-[10px] border border-border p-4";
        const wrapperStyle = c.alert ? { background: "#DC2626" } : undefined;
        const labelCls = c.alert ? "text-white/85 text-[11px] uppercase tracking-wider font-bold" : "field-label";
        const valueCls = c.alert ? "text-[22px] font-bold text-white leading-none" : "text-[22px] font-bold text-foreground leading-none";
        const subCls = c.alert ? "text-[11.5px] text-white/85 mt-2" : "help-text mt-2";
        const unitCls = c.alert ? "text-[12px] font-medium text-white/85" : "text-[12px] font-medium text-muted";
        return (
          <article key={c.label}
                   data-testid={`meteo-card-${c.label.toLowerCase().replace(/[^a-z]/g, "")}${c.alert ? "-alert" : ""}`}
                   className={wrapperCls} style={wrapperStyle}>
            <header className="flex items-center gap-2 mb-2">
              <span className={`icon-chip ${c.alert ? "" : "icon-chip-" + c.tone} !w-7 !h-7`}
                    style={c.alert ? { background: "rgba(255,255,255,0.18)", color: "white" } : undefined}>
                <Icon className="w-3.5 h-3.5" weight="regular" />
              </span>
              <span className={labelCls}>{c.label}</span>
              {c.alert && (
                <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-white/20 px-1.5 py-0.5 rounded">
                  Alerta
                </span>
              )}
            </header>
            <p className={valueCls}>
              {c.value} <span className={unitCls}>{c.unit}</span>
            </p>
            <p className={subCls}>{c.sub}</p>
          </article>
        );
      })}
    </section>
  );
}

function DaysTable({ days }) {
  return (
    <section className="bg-white rounded-[10px] border border-border p-4 md:p-5"
             data-testid="meteo-days-table">
      <header className="flex items-center gap-2 mb-3">
        <span className="icon-chip icon-chip-brand !w-7 !h-7">
          <CalendarBlank className="w-3.5 h-3.5" weight="regular" />
        </span>
        <h3 className="section-title">Detalle por día</h3>
        <span className="ml-auto text-[11.5px] text-muted">
          {days.length} día{days.length === 1 ? "" : "s"}
        </span>
      </header>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-[12.5px]">
          <thead className="text-muted text-[11px]">
            <tr className="border-b border-border">
              <th className="text-left font-semibold py-2 px-2">Fecha</th>
              <th className="text-right font-semibold py-2 px-2">Viento</th>
              <th className="text-right font-semibold py-2 px-2">Ráfaga</th>
              <th className="text-center font-semibold py-2 px-2">Dir.</th>
              <th className="text-right font-semibold py-2 px-2">Precipitación</th>
              <th className="text-right font-semibold py-2 px-2">Tª mín/máx</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => {
              const alert = d.severe;
              const rowCls = alert
                ? "bg-red-50 text-red-900 font-semibold"
                : "border-t border-border";
              return (
                <tr key={d.date} className={rowCls} data-testid={`meteo-day-${d.date}${alert ? "-severe" : ""}`}>
                  <td className="py-2 px-2 whitespace-nowrap">{shortDateES(d.date)}</td>
                  <td className="py-2 px-2 text-right">{fmt(d.viento_max_kmh, " km/h")}</td>
                  <td className="py-2 px-2 text-right">{fmt(d.rafaga_max_kmh, " km/h")}</td>
                  <td className="py-2 px-2 text-center text-muted">{d.viento_direccion_text || "—"}</td>
                  <td className="py-2 px-2 text-right">
                    {fmt(d.precipitacion_mm, " mm")}
                    <span className="text-muted text-[10px] ml-1">({Math.round(d.precipitacion_horas || 0)}h)</span>
                  </td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    {fmt(d.temperatura_min, "°")} / {fmt(d.temperatura_max, "°")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="help-text mt-3 italic">
        Filas en rojo: viento o ráfaga ≥ {SEVERE_WIND_KMH} km/h, o precipitación &gt; {SEVERE_PRECIP_MM} mm.
      </p>
    </section>
  );
}

function fmt(v, suffix = "", decimals = 1) {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `${n.toFixed(decimals)}${suffix}`;
}

function shortDateES(iso) {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

function ExternalLinks({ lat, lon, mapaderayosUrl }) {
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="meteo-external-links">
      <a href={mapaderayosUrl} target="_blank" rel="noopener noreferrer"
         data-testid="meteo-mapaderayos-link"
         className="bg-white rounded-[10px] border border-border p-4 hover:border-brand transition-colors group">
        <header className="flex items-center gap-2 mb-1">
          <span className="icon-chip icon-chip-brand !w-7 !h-7">
            <CloudLightning className="w-3.5 h-3.5" weight="regular" />
          </span>
          <span className="font-semibold text-[13px] text-foreground">mapaderayos.com</span>
          <ArrowSquareOut className="w-3.5 h-3.5 text-muted ml-auto group-hover:text-brand" />
        </header>
        <p className="help-text">Visor en vivo de rayos centrado en estas coordenadas. Útil para tormentas recientes.</p>
      </a>
      <a href={`https://www.aemet.es/es/eltiempo/observacion/ultimosdatos?l=&w=0`}
         target="_blank" rel="noopener noreferrer"
         data-testid="meteo-aemet-link"
         className="bg-white rounded-[10px] border border-border p-4 hover:border-brand transition-colors group">
        <header className="flex items-center gap-2 mb-1">
          <span className="icon-chip icon-chip-amber !w-7 !h-7">
            <Cloud className="w-3.5 h-3.5" weight="regular" />
          </span>
          <span className="font-semibold text-[13px] text-foreground">AEMET — últimos datos</span>
          <ArrowSquareOut className="w-3.5 h-3.5 text-muted ml-auto group-hover:text-brand" />
        </header>
        <p className="help-text">Web oficial de la Agencia Estatal de Meteorología (España) para validación legal.</p>
      </a>
    </section>
  );
}

function MeteosimOptions({ vars, setVars, status }) {
  const configured = !!status?.configured;
  const items = [
    { id: "rayos", label: "Rayos", description: "Mapa histórico de actividad eléctrica" },
    { id: "racha_viento", label: "Racha de viento", description: "Pico horario de racha" },
    { id: "prec_acumulada", label: "Precipitación acumulada", description: "Total de lluvia en el rango" },
    { id: "prec_intensidad", label: "Precipitación intensidad", description: "Intensidad horaria (l/m²/h)" },
  ];
  return (
    <section className="bg-white rounded-[10px] border border-border p-4 md:p-5"
             data-testid="meteo-meteosim-options">
      <header className="flex items-center gap-2 mb-2">
        <span className="icon-chip icon-chip-purple !w-7 !h-7">
          <Cloud className="w-3.5 h-3.5" weight="regular" />
        </span>
        <h3 className="section-title">Informes oficiales Meteosim (anexos al PDF)</h3>
        {configured ? (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded bg-status-success-bg text-status-success-fg text-[10.5px] font-semibold uppercase tracking-wider">
            Activa
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded bg-status-warning-bg text-status-warning-fg text-[10.5px] font-semibold uppercase tracking-wider">
            Sin credenciales
          </span>
        )}
      </header>
      {!configured ? (
        <p className="help-text">
          Meteosim aún no está configurado. Un administrador debe ir a
          <b> Configuración → Integraciones de terceros</b> y guardar las
          credenciales corporativas. Tras eso, esta sección permitirá anexar
          los PDFs oficiales de Meteosim al informe.
        </p>
      ) : (
        <>
          <p className="help-text mb-3">
            Marca las variables que quieras añadir. Cada variable seleccionada
            descarga el PDF oficial de Meteosim (con su sello) y se concatena
            al final de este informe. Puede tardar 30-60 s.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {items.map((it) => (
              <label key={it.id}
                     className="flex items-start gap-3 cursor-pointer select-none p-2 -mx-1 rounded-md hover:bg-slate-50"
                     data-testid={`meteo-meteosim-toggle-${it.id}-label`}>
                <input type="checkbox"
                       checked={!!vars[it.id]}
                       onChange={(e) => setVars({ ...vars, [it.id]: e.target.checked })}
                       data-testid={`meteo-meteosim-toggle-${it.id}`}
                       className="mt-0.5 w-4 h-4 accent-purple-600" />
                <span className="flex-1">
                  <span className="block text-foreground font-semibold text-[13px]">{it.label}</span>
                  <span className="block help-text">{it.description}</span>
                </span>
              </label>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function LightningMapsOptions({
  includeLightningmaps, setIncludeLightningmaps,
  includeMapaderayos, setIncludeMapaderayos,
  isMapaderayosEligible,
}) {
  return (
    <section className="bg-white rounded-[10px] border border-border p-4 md:p-5"
             data-testid="meteo-lightning-options">
      <header className="flex items-center gap-2 mb-3">
        <span className="icon-chip icon-chip-amber !w-7 !h-7">
          <CloudLightning className="w-3.5 h-3.5" weight="regular" />
        </span>
        <h3 className="section-title">Mapas de rayos en el PDF (opcional)</h3>
      </header>
      <p className="help-text mb-3">
        Añade páginas extra al final del informe con capturas de visores de rayos.
        Útil cuando el siniestro pueda estar relacionado con caída de rayo.
      </p>
      <div className="space-y-2">
        <label className="flex items-start gap-3 cursor-pointer select-none p-2 -mx-2 rounded-md hover:bg-slate-50"
               data-testid="meteo-toggle-lightningmaps-label">
          <input type="checkbox"
                 checked={includeLightningmaps}
                 onChange={(e) => setIncludeLightningmaps(e.target.checked)}
                 data-testid="meteo-toggle-lightningmaps"
                 className="mt-0.5 w-4 h-4 accent-amber-500" />
          <span className="flex-1">
            <span className="block text-foreground font-semibold text-[13px]">
              LightningMaps.org — archivo histórico
            </span>
            <span className="block help-text">
              Imagen del archivo público de Blitzortung/LightningMaps para el día consultado.
              Cubre Europa Occidental. Recomendado para evidencia histórica.
            </span>
          </span>
        </label>

        <label className={`flex items-start gap-3 select-none p-2 -mx-2 rounded-md ${
                isMapaderayosEligible ? "cursor-pointer hover:bg-slate-50" : "cursor-not-allowed opacity-55"}`}
               data-testid="meteo-toggle-mapaderayos-label">
          <input type="checkbox"
                 checked={includeMapaderayos && isMapaderayosEligible}
                 onChange={(e) => setIncludeMapaderayos(e.target.checked)}
                 disabled={!isMapaderayosEligible}
                 data-testid="meteo-toggle-mapaderayos"
                 className="mt-0.5 w-4 h-4 accent-amber-500 disabled:cursor-not-allowed" />
          <span className="flex-1">
            <span className="block text-foreground font-semibold text-[13px]">
              mapaderayos.com — visor en vivo
              {!isMapaderayosEligible && (
                <span className="ml-2 text-[10.5px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                  Solo &lt; 24h
                </span>
              )}
            </span>
            <span className="block help-text">
              {isMapaderayosEligible
                ? "Captura del visor en directo centrado en estas coordenadas. No es histórico: refleja el estado en el momento de generar el informe."
                : "No disponible: este visor sólo aporta información útil si la fecha consultada es de las últimas 24h."}
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}

function PdfFooter({ expedienteId, onGenerate, generating }) {
  return (
    <section className="bg-white rounded-[10px] border border-border p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center gap-3"
             data-testid="meteo-pdf-footer">
      <Info className="w-5 h-5 text-muted flex-shrink-0" weight="regular" />
      <div className="flex-1">
        <p className="text-foreground font-semibold text-[13px]">Generar informe meteorológico</p>
        <p className="help-text mt-0.5">
          PDF con sello del gabinete (logo + CIF + nombre comercial), mapa, datos y enlace a mapaderayos.
          {expedienteId ? " Se adjuntará automáticamente al expediente como Evidencia meteo." : ""}
        </p>
      </div>
      <button onClick={onGenerate}
              disabled={generating}
              data-testid="meteo-generate-pdf-btn"
              className="bg-foreground text-white text-[13px] h-10 px-4 rounded-md font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
        {generating
          ? "Generando…"
          : <><DownloadSimple className="w-4 h-4" weight="bold" /> Generar PDF</>}
      </button>
    </section>
  );
}
